import React, { useState } from "react";
import {
  Modal,
  Header,
  Box,
  SpaceBetween,
  Button,
  Alert,
  Container,
  FormField,
  Textarea,
  Icon,
} from "@amzn/awsui-components-console";
import {
  Ard,
  ValidationError,
} from "@amzn/fuse-accounting-central-datastore-client";

// Override Modal Props
interface OverrideModalProps {
  visible: boolean;
  onDismiss: () => void;
  onSubmit: () => void;
  overrideReason: string;
  setOverrideReason: (reason: string) => void;
  validationErrors: ValidationError[];
  isSubmitting: boolean;
}

// Save Modal Props
interface SaveModalProps {
  visible: boolean;
  onDismiss: () => void;
  onSubmit: () => void;
  saveReason: string;
  setSaveReason: (reason: string) => void;
  isEditMode: boolean;
}

export const OverrideModal: React.FC<OverrideModalProps> = ({
  visible,
  onDismiss,
  onSubmit,
  overrideReason,
  setOverrideReason,
  validationErrors,
  isSubmitting,
}) => {
  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      size="medium"
      header={<Header>Override Validation and Submit ARD</Header>}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={onSubmit}
              disabled={!overrideReason.trim() || isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit with Override"}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween direction="vertical" size="l">
        <Alert type="warning" header="Override Validation">
          You are about to submit an ARD that has failed validation. This action
          will bypass the validation errors and submit the ARD directly to the
          system.
          <br />
          <br />
          <strong>Validation Errors Found: {validationErrors.length}</strong>
          <br />
          <br />
          Please provide a detailed reason for overriding the validation.
        </Alert>

        <FormField
          label="Override Reason"
          description="Please provide a detailed explanation for why you are overriding the validation errors."
        >
          <Textarea
            value={overrideReason}
            onChange={({ detail }) => setOverrideReason(detail.value)}
            placeholder="Enter your reason for overriding validation (required)"
            rows={4}
          />
        </FormField>

        {validationErrors.length > 0 && (
          <Container>
            <Header variant="h3">Validation Errors Being Overridden:</Header>
            <SpaceBetween direction="vertical" size="xs">
              {Array.from(
                new Set(validationErrors.map((error) => error.message)),
              ).map((message, index) => (
                <div key={index} style={{ color: "#d13212" }}>
                  • {message}
                </div>
              ))}
            </SpaceBetween>
          </Container>
        )}
      </SpaceBetween>
    </Modal>
  );
};

export const SaveModal: React.FC<SaveModalProps> = ({
  visible,
  onDismiss,
  onSubmit,
  saveReason,
  setSaveReason,
  isEditMode,
}) => {
  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      size="medium"
      header={
        <Header>{isEditMode ? "Save ARD Changes" : "Save New ARD"}</Header>
      }
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={onSubmit}
              disabled={!saveReason.trim()}
            >
              {isEditMode ? "Save ARD" : "Save New ARD"}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween direction="vertical" size="l">
        <FormField
          label="Save Reason"
          description="Please provide a detailed explanation for the save."
        >
          <Textarea
            value={saveReason}
            onChange={({ detail }) => setSaveReason(detail.value)}
            placeholder="Enter your reason for saving (required)"
            rows={4}
          />
        </FormField>
      </SpaceBetween>
    </Modal>
  );
};

// USL Line Delete Confirmation Modal Props
interface UslLineDeleteModalProps {
  visible: boolean;
  onDismiss: () => void;
  onConfirm: () => void;
  formData: Ard | null;
  pendingDeletion: { useCaseIndex: number; lineIndex: number } | null;
}

export const UslLineDeleteModal: React.FC<UslLineDeleteModalProps> = ({
  visible,
  onDismiss,
  onConfirm,
  formData,
  pendingDeletion,
}) => {
  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      size="medium"
      header={<Header>Confirm USL Line Deletion</Header>}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss}>
              Cancel
            </Button>
            <Button variant="primary" onClick={onConfirm}>
              Delete USL Line
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween direction="vertical" size="l">
        <Alert type="warning" header="Delete USL Line">
          Are you sure you want to delete this USL line? This action cannot be
          undone.
        </Alert>

        {pendingDeletion && formData?.usecases && (
          <Container>
            <Header variant="h3">USL Line Details:</Header>
            <SpaceBetween direction="vertical" size="xs">
              <div>
                <strong>Use Case:</strong>{" "}
                {formData.usecases[pendingDeletion.useCaseIndex]?.name}
              </div>
              <div>
                <strong>Line Index:</strong> {pendingDeletion.lineIndex + 1}
              </div>
            </SpaceBetween>
          </Container>
        )}
      </SpaceBetween>
    </Modal>
  );
};

// Use Case Delete Confirmation Modal Props
interface UseCaseDeleteModalProps {
  visible: boolean;
  onDismiss: () => void;
  onConfirm: () => void;
  formData: Ard | null;
  pendingDeletion: { useCaseIndex: number; activeTabId: string } | null;
}

export const UseCaseDeleteModal: React.FC<UseCaseDeleteModalProps> = ({
  visible,
  onDismiss,
  onConfirm,
  formData,
  pendingDeletion,
}) => {
  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      size="medium"
      header={<Header>Confirm Use Case Deletion</Header>}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss}>
              Cancel
            </Button>
            <Button variant="primary" onClick={onConfirm}>
              Delete Use Case
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween direction="vertical" size="l">
        <Alert type="warning" header="Delete Use Case">
          Are you sure you want to delete this entire use case? This will remove
          all USL lines associated with this use case. This action cannot be
          undone.
        </Alert>

        {pendingDeletion && formData?.usecases && (
          <Container>
            <Header variant="h3">Use Case Details:</Header>
            <SpaceBetween direction="vertical" size="xs">
              <div>
                <strong>Use Case Name:</strong>{" "}
                {formData.usecases[pendingDeletion.useCaseIndex]?.name}
              </div>
              <div>
                <strong>Number of USL Lines:</strong>{" "}
                {formData.usecases[pendingDeletion.useCaseIndex]?.uslLines
                  ?.length || 0}
              </div>
            </SpaceBetween>
          </Container>
        )}
      </SpaceBetween>
    </Modal>
  );
};

// Success Modal Props
interface SuccessModalProps {
  visible: boolean;
  onDismiss: () => void;
  ardId: string;
  isEditMode: boolean;
  isOverride?: boolean;
}

export const SuccessModal: React.FC<SuccessModalProps> = ({
  visible,
  onDismiss,
  ardId,
  isEditMode,
  isOverride = false,
}) => {
  const [copySuccess, setCopySuccess] = useState(false);

  const handleCopyArdId = async () => {
    try {
      await navigator.clipboard.writeText(ardId);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000); // Reset after 2 seconds
    } catch (error) {
      console.error("Failed to copy ARD ID:", error);
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = ardId;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand("copy");
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (fallbackError) {
        console.error("Fallback copy failed:", fallbackError);
      }
      document.body.removeChild(textArea);
    }
  };

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      size="medium"
      header={
        <Header>
          <SpaceBetween direction="horizontal" size="s" alignItems="center">
            <Icon name="status-positive" variant="success" />
            ARD {isEditMode ? "Updated" : "Created"} Successfully
          </SpaceBetween>
        </Header>
      }
      footer={
        <Box float="right">
          <Button variant="primary" onClick={onDismiss}>
            Close
          </Button>
        </Box>
      }
    >
      <SpaceBetween direction="vertical" size="l">
        <Alert
          type="success"
          header={`ARD ${isEditMode ? "Update" : "Creation"} Successful`}
        >
          Your ARD has been {isEditMode ? "updated" : "created"} successfully
          {isOverride ? " with validation override" : ""}.
        </Alert>

        <Container>
          <Header variant="h3">ARD Details:</Header>
          <SpaceBetween direction="vertical" size="xs">
            <div>
              <strong>ARD ID:</strong>{" "}
              <span
                style={{
                  cursor: "pointer",
                  color: "#0073bb",
                  textDecoration: "underline",
                  userSelect: "all",
                }}
                onClick={handleCopyArdId}
                title="Click to copy ARD ID to clipboard"
              >
                {ardId}
              </span>
              {copySuccess && (
                <span
                  style={{
                    marginLeft: "8px",
                    color: "#037f0c",
                    fontSize: "12px",
                  }}
                >
                  <Icon name="status-positive" size="inherit" /> Copied!
                </span>
              )}
            </div>
            <div>
              <strong>Operation:</strong> {isEditMode ? "Update" : "Create"}
            </div>
            <div>
              <strong>Status:</strong>{" "}
              {isOverride ? "Submitted with Override" : "Submitted"}
            </div>
          </SpaceBetween>
        </Container>
      </SpaceBetween>
    </Modal>
  );
};

// Failure Modal Props
interface FailureModalProps {
  visible: boolean;
  onDismiss: () => void;
  errorMessage: string;
  isEditMode: boolean;
}

export const FailureModal: React.FC<FailureModalProps> = ({
  visible,
  onDismiss,
  errorMessage,
  isEditMode,
}) => {
  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      size="medium"
      header={
        <Header>
          <SpaceBetween direction="horizontal" size="s" alignItems="center">
            <Icon name="status-negative" variant="error" />
            ARD {isEditMode ? "Update" : "Creation"} Failed
          </SpaceBetween>
        </Header>
      }
      footer={
        <Box float="right">
          <Button variant="primary" onClick={onDismiss}>
            Close
          </Button>
        </Box>
      }
    >
      <SpaceBetween direction="vertical" size="l">
        <Alert
          type="error"
          header={`ARD ${isEditMode ? "Update" : "Creation"} Failed`}
        >
          An error occurred while {isEditMode ? "updating" : "creating"} your
          ARD. Please review the error details below and try again.
        </Alert>

        <Container>
          <Header variant="h3">Error Details:</Header>
          <Box color="text-status-error" fontSize="body-s">
            {errorMessage}
          </Box>
        </Container>
      </SpaceBetween>
    </Modal>
  );
};
